import React from "react";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useI18n } from "@/i18n/I18nContext";

import { WIZARD } from "@/constants/testIds";
import DatePickerField from "@/components/wizard/DatePickerField";
import VoiceRecorder from "@/components/voice/VoiceRecorder";

const MESSAGE_POSITIONS = ["none", "debut", "fin", "les_deux"];
const TRANSLATION_LANGUAGES = ["fr", "en", "es", "de", "it"];

export default function Step4Personalization({ form, update, pricingConfig }) {
  const { t, language } = useI18n();
  const fee = () => null;
  return (
    <div className="space-y-8">
      <div>
        <Label className="text-base">{t("wizard.messageLabel")} {fee("message_fee")}</Label>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {MESSAGE_POSITIONS.map((p) => (
            <button
              key={p}
              type="button"
              data-testid={WIZARD.messagePosition(p)}
              onClick={() => update({ message_position: p })}
              className={`rounded-xl border px-3 py-3 text-sm font-medium transition-all ${
                form.message_position === p ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/40"
              }`}
            >
              {t(`wizard.message${p === "none" ? "None" : p === "debut" ? "Start" : p === "fin" ? "End" : "Both"}`)}
            </button>
          ))}
        </div>
        {form.message_position !== "none" && (
          <Textarea
            data-testid={WIZARD.messageTextInput}
            placeholder={t("wizard.messageTextPlaceholder")}
            value={form.message_text}
            onChange={(e) => update({ message_text: e.target.value })}
            className="mt-3 rounded-xl"
          />
        )}
      </div>

      <div className="rounded-2xl border border-primary/30 bg-primary/5 p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <Label className="text-base">{t("wizard.voiceMessageLabel")}</Label>
              <span className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary-foreground">
                {t("landing.newBadge")}
              </span>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">{t("wizard.voiceMessageDesc")}</p>
          </div>
          <Switch
            data-testid={WIZARD.voiceMessageSwitch}
            checked={form.personal_voice_message}
            onCheckedChange={(v) => update({ personal_voice_message: v })}
          />
        </div>
        {form.personal_voice_message && (
          <div className="mt-5 space-y-5">
            <VoiceRecorder />
            <div>
              <Label className="text-sm text-muted-foreground">{t("wizard.voiceMessageTextLabel")}</Label>
              <Textarea
                data-testid={WIZARD.voiceMessageTextInput}
                maxLength={400}
                placeholder={t("wizard.voiceMessageTextPlaceholder")}
                value={form.voice_message_text}
                onChange={(e) => update({ voice_message_text: e.target.value })}
                className="mt-1.5 rounded-xl"
              />
              <p className="mt-1 text-right text-xs text-muted-foreground">{(form.voice_message_text || "").length}/400</p>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">{t("wizard.voiceMessagePositionLabel")}</Label>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {["intro", "outro", "both"].map((p) => (
                  <button
                    key={p}
                    type="button"
                    data-testid={WIZARD.voiceMessagePosition(p)}
                    onClick={() => update({ voice_message_position: p })}
                    className={`rounded-xl border px-3 py-3 text-sm font-medium transition-all ${
                      form.voice_message_position === p ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/40"
                    }`}
                  >
                    {t(`wizard.voiceMessagePosition${p.charAt(0).toUpperCase()}${p.slice(1)}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-border bg-card/50 p-5">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-base">{t("wizard.giftLabel")} {fee("gift_fee")}</Label>
            <p className="mt-1 text-sm text-muted-foreground">{t("wizard.giftDesc")}</p>
          </div>
          <Switch data-testid={WIZARD.giftSwitch} checked={form.gift_enabled} onCheckedChange={(v) => update({ gift_enabled: v })} />
        </div>
        {form.gift_enabled && (
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input data-testid={WIZARD.giftName} placeholder={t("wizard.giftNameLabel")} value={form.gift_name} onChange={(e) => update({ gift_name: e.target.value })} className="rounded-xl" />
            <Input data-testid={WIZARD.giftEmail} type="email" placeholder={t("wizard.giftEmailLabel")} value={form.gift_email} onChange={(e) => update({ gift_email: e.target.value })} className="rounded-xl" />
            <Textarea data-testid={WIZARD.giftMessage} placeholder={t("wizard.giftMessageLabel")} value={form.gift_message} onChange={(e) => update({ gift_message: e.target.value })} className="rounded-xl sm:col-span-2" />
            <DatePickerField testId={WIZARD.giftDate} value={form.gift_send_date} onChange={(v) => update({ gift_send_date: v })} placeholder={t("wizard.giftDateLabel")} minDate={new Date()} />
          </div>
        )}
      </div>

      <div className="flex items-center justify-between rounded-2xl border border-border bg-card/50 p-5">
        <div>
          <Label className="text-base">{t("wizard.youtubeLabel")} {fee("youtube_publish_fee")}</Label>
          <p className="mt-1 text-sm text-muted-foreground">{t("wizard.youtubeDesc")}</p>
        </div>
        <Switch data-testid={WIZARD.youtubeSwitch} checked={form.youtube_publish_requested} onCheckedChange={(v) => update({ youtube_publish_requested: v })} />
      </div>

      <div className="flex items-center justify-between rounded-2xl border border-border bg-card/50 p-5">
        <div>
          <Label className="text-base">{t("wizard.downloadLabel")} {fee("download_mp3_fee")}</Label>
          <p className="mt-1 text-sm text-muted-foreground">{t("wizard.downloadDesc")}</p>
        </div>
        <Switch data-testid={WIZARD.downloadSwitch} checked={form.download_mp3_included} onCheckedChange={(v) => update({ download_mp3_included: v })} />
      </div>

      <div className="flex items-center justify-between rounded-2xl border border-primary/30 bg-primary/5 p-5">
        <div>
          <Label className="text-base">{t("wizard.rushLabel")} {fee("rush_generation_fee")}</Label>
          <p className="mt-1 text-sm text-muted-foreground">{t("wizard.rushDesc")}</p>
        </div>
        <Switch data-testid={WIZARD.rushSwitch} checked={form.rush_generation} onCheckedChange={(v) => update({ rush_generation: v })} />
      </div>

      {form.voice_enabled && (
        <div className="flex items-center justify-between rounded-2xl border border-border bg-card/50 p-5">
          <div>
            <Label className="text-base">{t("wizard.bonusInstrumentalLabel")} {fee("bonus_instrumental_fee")}</Label>
            <p className="mt-1 text-sm text-muted-foreground">{t("wizard.bonusInstrumentalDesc")}</p>
          </div>
          <Switch data-testid={WIZARD.bonusInstrumentalSwitch} checked={form.bonus_instrumental} onCheckedChange={(v) => update({ bonus_instrumental: v })} />
        </div>
      )}

      <div className="flex items-center justify-between rounded-2xl border border-primary/30 bg-primary/5 p-5">
        <div>
          <Label className="text-base">{t("wizard.socialShortLabel")} {fee("social_short_fee")}</Label>
          <p className="mt-1 text-sm text-muted-foreground">{t("wizard.socialShortDesc")}</p>
        </div>
        <Switch data-testid={WIZARD.socialShortSwitch} checked={form.social_short_format} onCheckedChange={(v) => update({ social_short_format: v })} />
      </div>

      <div className="rounded-2xl border border-border bg-card/50 p-5">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-base">{t("wizard.translateLabel")} {fee("translation_fee")}</Label>
            <p className="mt-1 text-sm text-muted-foreground">{t("wizard.translateDesc")}</p>
          </div>
          <Switch data-testid={WIZARD.translateSwitch} checked={form.translate_lyrics} onCheckedChange={(v) => update({ translate_lyrics: v })} />
        </div>
        {form.translate_lyrics && (
          <div className="mt-4">
            <Label className="text-sm text-muted-foreground">{t("wizard.translateLanguageLabel")}</Label>
            <Select value={form.translate_target_language} onValueChange={(v) => update({ translate_target_language: v })}>
              <SelectTrigger data-testid={WIZARD.translateLanguageSelect} className="mt-1.5 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TRANSLATION_LANGUAGES.map((lang) => (
                  <SelectItem key={lang} value={lang}>{t(`wizard.lang_${lang}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between rounded-2xl border border-border bg-card/50 p-5">
        <div>
          <Label className="text-base">{t("wizard.certificateLabel")} {fee("certificate_fee")}</Label>
          <p className="mt-1 text-sm text-muted-foreground">{t("wizard.certificateDesc")}</p>
        </div>
        <Switch data-testid={WIZARD.certificateSwitch} checked={form.gift_certificate} onCheckedChange={(v) => update({ gift_certificate: v })} />
      </div>

      <div className="flex items-center justify-between rounded-2xl border border-border bg-card/50 p-5">
        <div>
          <Label className="text-base">{t("wizard.galleryOptInLabel")}</Label>
          <p className="mt-1 text-sm text-muted-foreground">{t("wizard.galleryOptInDesc")}</p>
        </div>
        <Switch data-testid={WIZARD.galleryOptInSwitch} checked={form.public_gallery_opt_in} onCheckedChange={(v) => update({ public_gallery_opt_in: v })} />
      </div>
    </div>
  );
}
